import React, { useState, useEffect } from "react";
import logoiii from "../assets/logoiii.png";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, fetchUser } from "../redux/slices/authSlice";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

const Register = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [canSubmit, setCanSubmit] = useState(false);

  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // Validar email con expresión regular
  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Validar formulario
  useEffect(() => {
    const newErrors = {
      email: "",
      password: "",
      confirmPassword: "",
    };

    // Validar email
    if (formData.email && !validateEmail(formData.email)) {
      newErrors.email = "Por favor, ingresa un email válido";
    }

    // Validar contraseña
    if (formData.password && formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    // Validar confirmación de contraseña
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    setFormErrors(newErrors);

    // Verificar si se puede enviar el formulario
    const isValid =
      formData.name.trim() !== "" &&
      formData.email.trim() !== "" &&
      validateEmail(formData.email) &&
      formData.password.trim() !== "" &&
      formData.password.length >= 6 &&
      formData.password === formData.confirmPassword;

    setCanSubmit(isValid);
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) return;

    // Enviar solo los datos necesarios (sin confirmPassword)
    const { confirmPassword, ...registrationData } = formData;
    const result = await dispatch(registerUser(registrationData));

    if (result.meta.requestStatus === "fulfilled") {
      // Verificar si hay una ruta de redirección guardada
      const intendedMoviePath = localStorage.getItem("intendedMoviePath");
      
      // Asegurar que el token se guarde y el estado de autenticación se actualice
      await dispatch(fetchUser());
      
      if (intendedMoviePath) {
        // Limpiar la ruta guardada
        localStorage.removeItem("intendedMoviePath");
        navigate(intendedMoviePath);
      } else {
        navigate("/home");
      }
    }
  };

  return (
    <div
      className="bg-gray-900 text-white relative min-h-screen w-full flex items-center justify-center font-montserrat  overflow-hidden py-12 sm:py-20"
      style={{
        background: "linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 100%)",
        overflow: "hidden",
      }}
    >
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 20px, #000000 20px, #000000 40px, transparent 40px, transparent 100px)`,
          backgroundSize: "100px 100%",
          boxShadow: "inset 0 0 10px rgba(0,0,0,0.8)",
        }}
      />
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, transparent 0px, transparent 20px, rgba(0,0,0,0.5) 20px, rgba(0,0,0,0.5) 40px, transparent 40px, transparent 100px)`,
          backgroundSize: "100px 100%",
          filter: "blur(3px)",
          transform: "translateX(2px)",
        }}
      />

      <div
        className="p-4 sm:p-6 md:p-8 rounded-2xl w-full max-w-lg relative z-10 mt-2 sm:mt-20 md:mt-5"
        style={{
          border: "2px solid rgba(255, 255, 255, 0.7)",
          boxShadow: "0 0 20px rgba(255, 255, 255, 0.2)",
          background: "rgba(0, 0, 0, 0)",
          backdropFilter: "blur(5px)",
        }}
      >
        <div className="flex items-center justify-center mb-6">
          <img
            src={logoiii}
            alt="Logo"
            className="w-16 h-16 sm:w-24 sm:h-24 object-contain mr-2"
          />
          <span className="text-white font-bold text-lg sm:text-xl">
            EPIC MOVIE
          </span>
        </div>
        <h2 className="text-2xl font-bold mb-6 text-white text-center">
          Crear Cuenta
        </h2>

        {error && (
          <div className="mb-4 p-2 bg-red-900 text-red-200 text-sm rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Nombre
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full p-2 md:p-3 bg-transparent border border-white/30 rounded-md text-white"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className={`w-full p-2 md:p-3 bg-transparent border ${formErrors.email ? 'border-red-500' : 'border-white/30'} rounded-md text-white`}
              required
            />
            {formErrors.email && (
              <p className="mt-1 text-red-500 text-xs flex items-center">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {formErrors.email}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Contraseña
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              className={`w-full p-2 md:p-3 bg-transparent border ${formErrors.password ? 'border-red-500' : 'border-white/30'} rounded-md text-white`}
              required
              minLength={6}
            />
            {formErrors.password && (
              <p className="mt-1 text-red-500 text-xs flex items-center">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {formErrors.password}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Confirmar Contraseña
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) =>
                setFormData({ ...formData, confirmPassword: e.target.value })
              }
              className={`w-full p-2 md:p-3 bg-transparent border ${formErrors.confirmPassword ? 'border-red-500' : 'border-white/30'} rounded-md text-white`}
              required
            />
            {formErrors.confirmPassword && (
              <p className="mt-1 text-red-500 text-xs flex items-center">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {formErrors.confirmPassword}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={loading || !canSubmit}
            className={`w-full mt-6 py-2 md:py-3 px-6 ${canSubmit ? 'bg-yellow-400 hover:bg-yellow-500' : 'bg-gray-500 cursor-not-allowed'} text-black font-medium rounded-full transition-all duration-300 ease-in-out focus:outline-none focus:ring-4 focus:ring-yellow-300 focus:ring-opacity-50`}
          >
            {loading ? "Registrando..." : "REGISTRARSE"}
          </button>
        </form>

        <p className="mt-4 text-center text-white">
          Ya tenés una cuenta?{" "}
          <Link to="/" className="underline text-blue-500">
            Inicia sesión aquí
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
